'use client';

import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import Image from 'next/image';
import Link from 'next/link';
import {
  MapPin,
  Calendar,
  CalendarPlus,
  Clock,
  ChevronLeft,
  ChevronRight,
  Heart,
  Trophy,
  AlertCircle,
  AlertTriangle,
  Settings2,
  Navigation,
  Mail,
  ExternalLink,
  Loader2,
  Users,
} from 'lucide-react';
import { useTranslations, useFormatter } from 'next-intl';
import { keepPreviousData } from '@tanstack/react-query';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { ShareDropdown } from '@/components/share-dropdown';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion/accordion';
import { Label } from '@/components/ui/label/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog/dialog';
import { Badge } from '@/components/ui/badge/badge';
import { RankBadge } from '@/tenants/dgr/leaderboard/components/RankBadge';
import { Textarea } from '@/components/ui/textarea/textarea';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useRegional } from '@/components/providers/regional-provider';
import type { MapboxMap } from '@/types/mapbox';
import { useChangeMyRide, useMyDashboardStats } from '@/lib/queries/my-dashboard';
import { useRidesByLocation, useStatesByCountry } from '@/tenants/_shared/hooks/use-public-lookups';
import { useEventDonationStrategy } from '@/tenants/_shared/hooks/donation-strategies';
import { usePaymentReturn } from '@/tenants/_shared/hooks/usePaymentReturn';
import {
  DonationFormModal,
  DonationAmountCard,
  type DonationData,
} from '@/tenants/_shared/components';
import { SponsorLogosSection } from '@/tenants/_shared/components/SponsorLogosSection';
import { CountrySelect } from '@/components/ui/country-select/country-select';
import { Combobox } from '@/components/ui/combobox/combobox';

// Public Mapbox token for public-facing pages
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';

interface EventData {
  id: string;
  city: string;
  slug: string;
  state: string | null;
  stateCode: string | null;
  country: string;
  countrySlug: string;
  date: string;
  time: string | null;
  endTime: string | null;
  capacity: number | null;
  distance: number | null;
  distanceFormat: string | null;
  mapType: 'static' | 'coordinates' | null;
  mapLink: string | null;
  facebookEventUrl: string | null;
  participants: number;
  teams: number;
  raised: number;
  goal: number;
  registrationOpen: boolean;
  isPostponed: boolean;
  isCancelled: boolean;
  postponedDate: string | null;
  description: string | null;
  venue: {
    name: string | null;
    address: string | null;
    city: string | null;
    coordinates: string | null;
  };
  route: {
    startPlace: string | null;
    startTime: string | null;
    startDepartureTime: string | null;
    endPlace: string | null;
    endDepartureTime: string | null;
    meetingPoints: Array<{
      name: string;
      address?: string;
      time?: string | null;
      coordinates?: { lat: number; lng: number };
      notes?: string;
    }>;
  };
  hosts: {
    id: string;
    name: string;
    isPrimary: boolean;
    profileImageUrl: string | null;
    username: string | null;
  }[];
  topFundraisers: Array<{ name: string; username: string; raised: number; image?: string }>;
}

interface ScheduleTimelineItem {
  time: string;
  title: string;
  location: string | null;
}

interface CountryOption {
  code: string;
  name: string;
}

interface StateData {
  id: string;
  code: string;
  name: string;
}

interface RideData {
  id: string;
  name: string;
  slug: string;
  capacity: string | null;
  minimumDonation: string | null;
  currentRegistrations: number;
}

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatEventDate(value: string | null): string {
  if (!value) return 'TBC';
  // Extract date parts directly — never create a Date object to avoid
  // timezone shift (UTC midnight → previous day in UTC-negative timezones).
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return 'TBC';
  const [, yearStr, monthStr, dayStr] = match;
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (month < 1 || month > 12 || day < 1 || day > 31) return 'TBC';
  return `${MONTH_NAMES[month - 1]} ${day}, ${yearStr}`;
}

function formatTime(value: string | null): string | null {
  if (!value) return null;
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value;
  const period = hours >= 12 ? 'PM' : 'AM';
  const normalized = hours % 12 || 12;
  return `${normalized}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function buildRegisterRideHref(countrySlug: string, eventSlug: string, stateCode?: string | null) {
  const params = new URLSearchParams({
    country: countrySlug,
    eventId: eventSlug,
  });

  if (stateCode) {
    params.set('state', stateCode);
  }

  return `/register?${params.toString()}`;
}

function parseTime24(value: string | null): { hours: number; minutes: number } | null {
  if (!value) return null;
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return { hours, minutes };
}

function toLocalDateTimeString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function toCalendarCompactString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

function parseRideMinimumDonation(minimumDonation: string | null | undefined): number {
  if (!minimumDonation) return 0;
  const parsed = Number(minimumDonation);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function getCapacityBadgeValue(capacity: string | null | undefined): string | null {
  if (!capacity) return null;
  const trimmed = capacity.trim();
  if (!trimmed || trimmed === '0') return null;
  return trimmed;
}

function parseRideCapacity(capacity: string | null | undefined): number | null {
  if (!capacity) return null;
  const parsed = Number(capacity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

interface EventPageProps {
  countrySlug?: string;
  eventSlug?: string;
  initialCountries?: CountryOption[];
  initialEvent?: EventData;
  initialTopFundraisers?: Array<{ name: string; username: string; raised: number; image?: string }>;
}

export function EventPage({
  countrySlug = 'australia',
  eventSlug = 'sydney',
  initialCountries,
  initialEvent,
  initialTopFundraisers,
}: EventPageProps) {
  const t = useTranslations('events');
  const tMy = useTranslations('my.events');
  const tCommon = useTranslations('common');
  const tDonate = useTranslations('donate.form');
  const registerT = useTranslations('register');
  const format = useFormatter();
  const {
    userType,
    isAuthenticated,
    eventAdmins: managedEvents,
  } = useAuth({
    hydrateFromQuery: true,
  });
  const { currency: regionalCurrency } = useRegional();
  const [contactMessage, setContactMessage] = useState('');
  const [contactHostsStatus, setContactHostsStatus] = useState<'idle' | 'success' | 'error'>(
    'idle',
  );
  const [isChangeRideOpen, setIsChangeRideOpen] = useState(false);
  const [changeRideError, setChangeRideError] = useState<string | null>(null);
  const [changeRideSuccess, setChangeRideSuccess] = useState<string | null>(null);
  const [changeRideCountryCode, setChangeRideCountryCode] = useState('');
  const [changeRideStateCode, setChangeRideStateCode] = useState('');
  const [changeRideRideId, setChangeRideRideId] = useState('');
  const [donorPage, setDonorPage] = useState(0);
  const donorPageSize = 10;

  // Map state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  const { data: countriesData = [], isLoading: isLoadingCountries } =
    trpc.publicLookups.countries.useQuery(undefined, {
      ...(initialCountries ? { initialData: initialCountries as never } : {}),
    });
  const selectedCountry = (countriesData as CountryOption[]).find(
    (country) => toSlug(country.name) === countrySlug,
  );
  const hasResolvedCountry = !isLoadingCountries;
  const canLookupEvent = Boolean(selectedCountry?.code);

  const { data: eventResponse, isLoading: isLoadingEvent } =
    trpc.publicLookups.eventBySlug.useQuery(
      { countryCode: selectedCountry?.code ?? '', eventSlug, currencyCode: regionalCurrency },
      {
        enabled: canLookupEvent,
        ...(initialEvent ? { initialData: initialEvent as never } : {}),
      },
    );

  const { data: donorsData, isLoading: isLoadingDonors } =
    trpc.publicLookups.eventDonations.useQuery(
      {
        eventSlug,
        countryCode: selectedCountry?.code ?? '',
        currencyCode: regionalCurrency,
        limit: donorPageSize,
        offset: donorPage * donorPageSize,
      },
      { enabled: canLookupEvent, placeholderData: keepPreviousData },
    );

  const { data: topFundraisersData = [] } = trpc.publicLookups.topFundraisersForEvent.useQuery(
    { eventId: eventResponse?.id ?? '', currencyCode: regionalCurrency },
    {
      enabled: !!eventResponse?.id,
      staleTime: 60_000,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      ...(initialTopFundraisers ? { initialData: initialTopFundraisers as never } : {}),
    },
  );

  // Fetch published route map data when mapType is coordinates
  const hasCoordinatesMap = eventResponse?.mapType === 'coordinates';
  const { data: routeMapData } = trpc.publicLookups.eventRouteMap.useQuery(
    { eventId: eventResponse?.id ?? '' },
    { enabled: hasCoordinatesMap && !!eventResponse?.id },
  );

  const isEntrantUser = userType === 'entrant';

  const { data: activeCampaign } = trpc.myDashboard.currentCampaign.useQuery(undefined, {
    enabled: isEntrantUser,
    retry: false,
  });
  const { data: stats } = useMyDashboardStats({ enabled: isEntrantUser });
  const changeRideMutation = useChangeMyRide();

  // Donation — redirect return detection (bank redirect / 3DS)
  const {
    returnDonationId,
    redirectResult,
    sessionId: returnSessionId,
    eventId: returnEventId,
    recoveryToken,
    isLocalStorageRecovery,
  } = usePaymentReturn();

  // Event donation strategy
  const donationEventId = eventResponse?.id || returnEventId || undefined;
  const strategy = useEventDonationStrategy(donationEventId);

  // Donation modal state
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [donateInitialAmount, setDonateInitialAmount] = useState<number | null>(null);

  const utils = trpc.useUtils();

  const contactHostsMutation = trpc.myEvent.contactHosts.useMutation({
    onSuccess: () => {
      setContactMessage('');
      setContactHostsStatus('success');
    },
    onError: () => {
      setContactHostsStatus('error');
    },
  });

  const invalidateEventQueries = useCallback(() => {
    void utils.publicLookups.eventBySlug.invalidate();
    void utils.publicLookups.eventDonations.invalidate();
    void utils.publicLookups.topFundraisersForEvent.invalidate();
  }, [utils]);

  const handleDonationSuccess = useCallback(
    (data: DonationData) => {
      // Convert donation amount from payment currency to display currency using FX rates
      const displayFxRate = eventResponse?.fxRate ?? 1;
      const paymentFxRate = strategy.paymentConfig?.paymentConfigs[0]?.fxRate ?? 1;
      const convertedAmount =
        paymentFxRate > 0 ? data.amount * (displayFxRate / paymentFxRate) : data.amount;

      // Optimistically update event totals
      const eventInput = {
        countryCode: selectedCountry?.code ?? '',
        eventSlug,
        currencyCode: regionalCurrency,
      };
      void utils.publicLookups.eventBySlug.cancel(eventInput);
      utils.publicLookups.eventBySlug.setData(eventInput, (old) => {
        if (!old) return old;
        return {
          ...old,
          raised: old.raised + convertedAmount,
          donationCount: old.donationCount + 1,
        };
      });

      // Optimistically prepend new donor to the first page of donations
      const donorsInput = {
        eventSlug,
        countryCode: selectedCountry?.code ?? '',
        currencyCode: regionalCurrency,
        limit: donorPageSize,
        offset: 0,
      };
      void utils.publicLookups.eventDonations.cancel(donorsInput);
      utils.publicLookups.eventDonations.setData(donorsInput, (old) => {
        if (!old) return old;
        return {
          ...old,
          donations: [
            {
              id: data.donationId ?? crypto.randomUUID(),
              donorName: data.anonymous ? 'Anonymous' : data.name,
              message: data.message ?? null,
              amount: data.amount,
              currencySymbol: strategy.currency.symbol,
              createdAt: new Date().toISOString(),
              isAnonymous: data.anonymous,
            },
            ...old.donations.slice(0, donorPageSize - 1),
          ],
          total: old.total + 1,
        };
      });

      // Reset to first page so donor sees their donation
      setDonorPage(0);

      // Delayed invalidation for eventual consistency after webhook processes
      setTimeout(() => {
        invalidateEventQueries();
      }, 5000);
    },
    [
      invalidateEventQueries,
      utils,
      selectedCountry?.code,
      eventSlug,
      regionalCurrency,
      donorPageSize,
      strategy.currency.symbol,
      eventResponse?.fxRate,
      strategy.paymentConfig,
    ],
  );

  const handleRedirectSuccess = useCallback(() => {
    invalidateEventQueries();
  }, [invalidateEventQueries]);

  const { data: statesData = [] as StateData[], isLoading: isLoadingStates } = useStatesByCountry(
    changeRideCountryCode || undefined,
  );
  const hasStates = statesData.length > 0;
  const { data: ridesData = [] as RideData[], isLoading: isLoadingRides } = useRidesByLocation(
    changeRideCountryCode || undefined,
    changeRideStateCode || undefined,
    hasStates,
  );

  const selectedRideData = useMemo(
    () => ridesData.find((ride: RideData) => ride.id === changeRideRideId),
    [ridesData, changeRideRideId],
  );
  const selectedRideMinimumDonation = parseRideMinimumDonation(selectedRideData?.minimumDonation);
  const selectedRideCapacity = parseRideCapacity(selectedRideData?.capacity);
  const selectedRideCurrentRegistrations = selectedRideData?.currentRegistrations ?? 0;
  const isSelectedRideFull =
    selectedRideCapacity !== null && selectedRideCurrentRegistrations >= selectedRideCapacity;
  const entrantTotalRaised = stats?.totalRaised ?? 0;
  const minimumDonationShortfall = Math.max(selectedRideMinimumDonation - entrantTotalRaised, 0);
  const isMinimumDonationMet = minimumDonationShortfall <= 0;
  const isSameRide = Boolean(
    activeCampaign?.eventId && activeCampaign.eventId === changeRideRideId,
  );
  const currencySymbol = '$';

  const isRegisteredRide = isEntrantUser ? activeCampaign?.eventSlug === eventSlug : false;
  const isEventAdminUser = userType === 'event_admin';
  const isManagedEvent =
    isEventAdminUser &&
    !!eventResponse?.id &&
    managedEvents.some((ea) => ea.eventId === eventResponse.id);
  const isAdminUser = isAuthenticated && !isEntrantUser && !isEventAdminUser;
  const canViewRideDetails = isAdminUser || isRegisteredRide || isManagedEvent;

  const eventData: EventData = {
    id: eventResponse?.id ?? 'unknown',
    city:
      eventResponse?.city ??
      eventSlug.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
    slug: eventSlug,
    state: eventResponse?.stateName ?? null,
    stateCode: eventResponse?.stateCode ?? null,
    country: eventResponse?.countryName ?? selectedCountry?.name ?? countrySlug,
    countrySlug,
    // When postponed and a new date is set, surface that everywhere the date renders
    // (header, hero) so visitors don't see the original date alongside the warning.
    date: formatEventDate(
      eventResponse?.isPostponed && eventResponse?.postponedDate
        ? eventResponse.postponedDate
        : (eventResponse?.eventDate ?? null),
    ),
    time: formatTime(eventResponse?.startTime ?? null),
    endTime: formatTime(eventResponse?.endTime ?? null),
    capacity: eventResponse?.capacity ?? null,
    distance: eventResponse?.distance ?? null,
    distanceFormat: eventResponse?.distanceFormat ?? null,
    mapType: eventResponse?.mapType ?? null,
    mapLink: eventResponse?.mapLink ?? null,
    facebookEventUrl: eventResponse?.facebookEventUrl ?? null,
    participants: eventResponse?.participantsCount ?? 0,
    teams: eventResponse?.teamsCount ?? 0,
    raised: eventResponse?.raised ?? 0,
    goal: eventResponse?.goal ?? 0,
    registrationOpen: !!eventResponse && !eventResponse.campaignClosed,
    isPostponed: eventResponse?.isPostponed ?? false,
    isCancelled: eventResponse?.isCancelled ?? false,
    postponedDate: eventResponse?.postponedDate ?? null,
    description: eventResponse?.description ?? null,
    venue: {
      name: eventResponse?.venue?.name ?? null,
      address: eventResponse?.venue?.address ?? null,
      city: eventResponse?.venue?.city ?? null,
      coordinates: eventResponse?.coordinates ?? null,
    },
    route: {
      startPlace: eventResponse?.route?.startPlace ?? null,
      startTime: formatTime(eventResponse?.route?.startTime ?? null),
      startDepartureTime: formatTime(eventResponse?.route?.startDepartureTime ?? null),
      endPlace: eventResponse?.route?.endPlace ?? null,
      endDepartureTime: formatTime(eventResponse?.route?.endDepartureTime ?? null),
      meetingPoints:
        eventResponse?.route?.meetingPoints?.map((point) => ({
          name: point.name,
          address: point.address,
          time: formatTime(point.time ?? null),
        })) ?? [],
    },
    hosts: eventResponse?.hosts ?? [],
    topFundraisers: topFundraisersData,
  };
  const registerRideHref = buildRegisterRideHref(
    eventData.countrySlug,
    eventData.slug,
    eventData.stateCode,
  );

  const formatCompactCurrency = (amount: number) =>
    formatPublicCurrency(format, amount, regionalCurrency);

  const hasDescription = Boolean(eventData.description?.trim());
  const hasRouteTimeline =
    !!eventData.route.startPlace ||
    !!eventData.route.endPlace ||
    eventData.route.meetingPoints.length > 0 ||
    !!eventData.route.startTime ||
    !!eventData.route.startDepartureTime ||
    !!eventData.route.endDepartureTime;
  const timelineItems: ScheduleTimelineItem[] = [];
  const startLocation = eventData.route.startPlace ?? eventData.venue.name ?? eventData.city;
  const finishLocation = eventData.route.endPlace ?? eventData.city;

  if (eventData.route.startTime) {
    timelineItems.push({
      time: eventData.route.startTime,
      title: t('meetingTime'),
      location: startLocation,
    });
  }

  if (eventData.route.startDepartureTime) {
    timelineItems.push({
      time: eventData.route.startDepartureTime,
      title: t('rideBegins'),
      location: startLocation,
    });
  }

  for (const point of eventData.route.meetingPoints) {
    if (!point.time && !point.name && !point.address) continue;
    timelineItems.push({
      time: point.time ?? '—',
      title: point.name || t('meetingPointLabel'),
      location: point.address || null,
    });
  }

  if (eventData.route.endDepartureTime) {
    timelineItems.push({
      time: eventData.route.endDepartureTime,
      title: t('arriveAtFinishVenue'),
      location: finishLocation,
    });
  }
  const hasTimelineItems = timelineItems.length > 0;

  const progressPercent =
    eventData.goal > 0 ? Math.min((eventData.raised / eventData.goal) * 100, 100) : 0;
  // Format postponed date as "March 15, 2026" without timezone shift (date column).
  const formattedPostponedDate = (() => {
    const raw = eventData.postponedDate;
    if (!raw) return null;
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, yyyy, mm, dd] = match.map(Number) as [unknown, number, number, number];
    return new Date(Date.UTC(yyyy, mm - 1, dd)).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  })();
  const eventStatusWarning = eventData.isCancelled
    ? t('eventCancelledWarning', { eventName: eventData.city })
    : eventData.isPostponed
      ? formattedPostponedDate
        ? t('eventPostponedToWarning', {
            eventName: eventData.city,
            date: formattedPostponedDate,
          })
        : t('eventPostponedWarning', { eventName: eventData.city })
      : null;
  const canShowChangeRide = isEntrantUser && !canViewRideDetails && Boolean(eventResponse?.id);
  const isSaveDisabled =
    !changeRideRideId ||
    isSameRide ||
    isSelectedRideFull ||
    !isMinimumDonationMet ||
    changeRideMutation.isPending;

  const openChangeRideModal = () => {
    if (!eventResponse) return;
    setChangeRideCountryCode(eventResponse.countryCode ?? '');
    setChangeRideStateCode(eventResponse.stateCode ?? '');
    setChangeRideRideId(eventResponse.id ?? '');
    setChangeRideError(null);
    setChangeRideSuccess(null);
    setIsChangeRideOpen(true);
  };

  const handleChangeRideSave = async () => {
    if (!changeRideRideId) return;
    setChangeRideError(null);
    setChangeRideSuccess(null);

    try {
      await changeRideMutation.mutateAsync({ eventId: changeRideRideId });
      setIsChangeRideOpen(false);
      setChangeRideSuccess(tMy('changeRideForm.success'));
    } catch (error) {
      setChangeRideError(error instanceof Error ? error.message : tCommon('error'));
    }
  };

  const mapPoint = useMemo(() => {
    const coords = eventData.venue.coordinates?.split(',').map((coord) => Number(coord.trim()));
    return coords && coords.length === 2 ? { lat: coords[0], lng: coords[1] } : null;
  }, [eventData.venue.coordinates]);
  const hasStaticMapLink =
    eventData.mapType === 'static' && !mapPoint && Boolean(eventData.mapLink);
  const hasRouteMap = canViewRideDetails && hasCoordinatesMap && !!routeMapData?.routeGeometry;
  // Calendar export should use the postponed date when the event has been
  // rescheduled — otherwise users would add the wrong date to their calendar.
  const calendarDateSource =
    eventResponse?.isPostponed && eventResponse?.postponedDate
      ? eventResponse.postponedDate
      : eventResponse?.eventDate;
  const eventDateValue =
    calendarDateSource && /^\d{4}-\d{2}-\d{2}/.test(calendarDateSource)
      ? calendarDateSource.slice(0, 10)
      : null;
  const [year, month, day] = (eventDateValue ?? '2026-05-17').split('-').map(Number);
  const calendarStartTime = parseTime24(eventResponse?.route?.startTime ?? null) ??
    parseTime24(eventResponse?.route?.startDepartureTime ?? null) ??
    parseTime24(eventResponse?.startTime ?? null) ?? { hours: 10, minutes: 0 };
  const calendarEndTime = parseTime24(eventResponse?.route?.endDepartureTime ?? null) ??
    parseTime24(eventResponse?.endTime ?? null) ?? {
      hours: Math.min(calendarStartTime.hours + 4, 23),
      minutes: calendarStartTime.minutes,
    };
  const calendarStart = new Date(
    year,
    (month || 1) - 1,
    day || 1,
    calendarStartTime.hours,
    calendarStartTime.minutes,
    0,
  );
  const calendarEnd = new Date(
    year,
    (month || 1) - 1,
    day || 1,
    calendarEndTime.hours,
    calendarEndTime.minutes,
    0,
  );
  const eventTitle = `DGR ${eventData.city}`;
  const eventDescription = `Distinguished Gentleman's Ride - ${eventData.city}`;
  const eventStartLocation = eventData.route.startPlace ?? eventData.venue.name ?? eventData.city;
  const eventLocation = [eventStartLocation, eventData.venue.address, eventData.venue.city]
    .filter(Boolean)
    .join(', ');
  const googleDates = `${toCalendarCompactString(calendarStart)}/${toCalendarCompactString(calendarEnd)}`;
  const outlookStart = toLocalDateTimeString(calendarStart);
  const outlookEnd = toLocalDateTimeString(calendarEnd);
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${toCalendarCompactString(calendarStart)}
DTEND:${toCalendarCompactString(calendarEnd)}
SUMMARY:${eventTitle}
DESCRIPTION:${eventDescription}
LOCATION:${eventLocation}
END:VEVENT
END:VCALENDAR`;
  const icsHref = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;

  // Load Mapbox GL JS
  const needsMapbox = (mapPoint && canViewRideDetails) || hasRouteMap;
  useEffect(() => {
    if (!MAPBOX_TOKEN || !needsMapbox) return;

    // Check if mapbox-gl is already loaded
    if (window.mapboxgl) {
      queueMicrotask(() => setMapLoaded(true));
      return;
    }

    // Load Mapbox GL JS CSS
    const link = document.createElement('link');
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Load Mapbox GL JS
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => {
      setMapLoaded(true);
    };
    document.head.appendChild(script);
  }, [needsMapbox]);

  // Initialize venue marker map (mapType !== 'coordinates')
  useEffect(() => {
    if (!mapLoaded || !mapContainer.current || !MAPBOX_TOKEN || !mapPoint || !canViewRideDetails) {
      return;
    }
    // Don't show venue marker map when we have a route map
    if (hasRouteMap) return;
    if (map.current) return;

    const mapboxgl = window.mapboxgl;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [mapPoint.lng, mapPoint.lat],
      zoom: 13,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      setMapInitialized(true);
      new mapboxgl.Marker({ color: 'hsl(45, 67%, 38%)' })
        .setLngLat([mapPoint.lng, mapPoint.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="padding: 8px; font-family: system-ui, sans-serif;">
              <p style="font-weight: 600; margin: 0; font-size: 14px;">${eventData.city}</p>
              <p style="margin: 4px 0 0; font-size: 12px; color: #666;">${eventData.venue.name ?? ''}</p>
            </div>
          `),
        )
        .addTo(map.current!);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setMapInitialized(false);
    };
  }, [canViewRideDetails, hasRouteMap, mapLoaded, mapPoint, eventData.city, eventData.venue.name]);

  // Initialize route map (mapType === 'coordinates' with published route)
  useEffect(() => {
    if (!mapLoaded || !mapContainer.current || !MAPBOX_TOKEN || !hasRouteMap || !routeMapData) {
      return;
    }
    if (map.current) return;

    const mapboxgl = window.mapboxgl;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const center = routeMapData.defaultCenter
      ? ([routeMapData.defaultCenter.lng, routeMapData.defaultCenter.lat] as [number, number])
      : routeMapData.routeGeometry?.coordinates?.[0]
        ? (routeMapData.routeGeometry.coordinates[0] as [number, number])
        : ([151.2093, -33.8688] as [number, number]);

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style:
        routeMapData.mapStyle === 'satellite'
          ? 'mapbox://styles/mapbox/satellite-streets-v12'
          : 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom: routeMapData.defaultZoom ?? 12,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      if (!map.current || !routeMapData.routeGeometry) return;
      setMapInitialized(true);

      // Draw route line
      map.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: routeMapData.routeGeometry,
        },
      });

      map.current.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#9a7b4f', 'line-width': 4, 'line-opacity': 0.9 },
      });

      // Fit bounds to route
      const coords = routeMapData.routeGeometry.coordinates as [number, number][];
      if (coords.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        for (const coord of coords) {
          bounds.extend(coord);
        }
        map.current.fitBounds(bounds.toArray(), { padding: 50, maxZoom: 15 });
      }

      // Add start/end waypoint markers
      if (routeMapData.waypoints?.length) {
        const sorted = [...routeMapData.waypoints].sort(
          (a: { order: number }, b: { order: number }) => a.order - b.order,
        );
        const first = sorted[0];
        const last = sorted[sorted.length - 1];

        if (first?.coordinates) {
          new mapboxgl.Marker({ color: '#22c55e' })
            .setLngLat([first.coordinates.lng, first.coordinates.lat])
            .addTo(map.current!);
        }
        if (last?.coordinates && sorted.length > 1) {
          new mapboxgl.Marker({ color: '#ef4444' })
            .setLngLat([last.coordinates.lng, last.coordinates.lat])
            .addTo(map.current!);
        }
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setMapInitialized(false);
    };
  }, [mapLoaded, hasRouteMap, routeMapData]);

  const leaderboardParams = new URLSearchParams();
  if (selectedCountry?.code) leaderboardParams.set('country', selectedCountry.code);
  if (eventResponse?.id) leaderboardParams.set('event', eventResponse.id);
  const leaderboardQs = leaderboardParams.toString();
  const leaderboardHref = `/leaderboard${leaderboardQs ? `?${leaderboardQs}` : ''}`;

  const showNotFoundState =
    hasResolvedCountry && !isLoadingEvent && (!canLookupEvent || !eventResponse);

  if (showNotFoundState) {
    return (
      <main className="flex-1 bg-dgr-cream">
        <div className="container max-w-4xl py-20 text-center">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">{t('noRidesFound')}</h1>
          <p className="text-stone-500">{t('noRidesDescription')}</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="flex-1">
        {/* Hero / Cover Section */}
        <section className="relative h-[320px] md:h-[420px]">
          <Image
            src="/images/dgr/HeroImage.jpg"
            alt={eventData.city}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

          {/* Hero Content */}
          <div className="absolute inset-0 flex items-end">
            <div className="container max-w-6xl pb-8">
              <Link
                href={`/events/${eventData.countrySlug}`}
                className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                {eventData.country}
              </Link>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {eventData.city}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-white/80">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {eventData.state ?? eventData.country}
                    </span>
                    <span className="text-white/40">•</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {eventData.date}
                    </span>
                    {eventData.time && (
                      <>
                        <span className="text-white/40">•</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {eventData.time}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {eventData.registrationOpen && !isAuthenticated && (
                    <Button asChild className="bg-dgr-gold hover:bg-dgr-gold-hover text-white">
                      <Link href={registerRideHref}>{t('registerNow')}</Link>
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                      >
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        {t('addToCalendar')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a
                          href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${encodeURIComponent(googleDates)}&details=${encodeURIComponent(eventDescription)}&location=${encodeURIComponent(eventLocation)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.5 22h-15A2.5 2.5 0 0 1 2 19.5v-15A2.5 2.5 0 0 1 4.5 2H6v1.5a.5.5 0 0 0 1 0V2h10v1.5a.5.5 0 0 0 1 0V2h1.5A2.5 2.5 0 0 1 22 4.5v15a2.5 2.5 0 0 1-2.5 2.5zM4.5 3A1.5 1.5 0 0 0 3 4.5v15A1.5 1.5 0 0 0 4.5 21h15a1.5 1.5 0 0 0 1.5-1.5v-15A1.5 1.5 0 0 0 19.5 3H18v.5a1.5 1.5 0 0 1-3 0V3H9v.5a1.5 1.5 0 0 1-3 0V3H4.5z" />
                            <path d="M7 11h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2zm-8 4h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
                          </svg>
                          {t('googleCalendar')}
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a
                          href={`https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(eventTitle)}&startdt=${encodeURIComponent(outlookStart)}&enddt=${encodeURIComponent(outlookEnd)}&body=${encodeURIComponent(eventDescription)}&location=${encodeURIComponent(eventLocation)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3h-3zm0 4.5v3h3v-3h-3zm0 4.5v1.83l3.05-1.83H18zm-5.25-9v3h3.75v-3h-3.75zm0 4.5v3h3.75v-3h-3.75zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73h-3.75zM9 3.75V6h2l.13.01.12.04v-2.3H9zM7.5 18h9.75l-5.5-3.4-4.25 2.56V18zm0-4.5l2.92-1.76L7.5 9.93v3.57z" />
                          </svg>
                          {t('outlook')}
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href={icsHref} download={`dgr-${eventData.slug}.ics`}>
                          <Calendar className="w-4 h-4 mr-3" />
                          {t('downloadIcs')}
                        </a>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ShareDropdown
                    url={typeof window !== 'undefined' ? window.location.href : ''}
                    title={eventTitle}
                    iconOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {eventStatusWarning && (
          <section
            className={cn(
              'border-b',
              eventData.isCancelled
                ? 'bg-red-50 border-red-300 text-red-900'
                : 'bg-amber-50 border-amber-300 text-amber-900',
            )}
          >
            <div className="container max-w-6xl py-3">
              <div className="flex items-start gap-3">
                <AlertTriangle
                  className={cn(
                    'w-5 h-5 mt-0.5 flex-shrink-0',
                    eventData.isCancelled ? 'text-red-600' : 'text-amber-600',
                  )}
                />
                <p className="text-sm font-medium">{eventStatusWarning}</p>
              </div>
            </div>
          </section>
        )}

        {/* Stats Bar */}
        <section className="border-b border-stone-200 bg-stone-50">
          <div className="container max-w-6xl py-6">
            {/* Mobile Layout */}
            <div className="md:hidden">
              {eventData.goal > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-dgr-gold-hover">
                        {formatCompactCurrency(eventData.raised)}
                      </p>
                      <p className="text-stone-500 text-sm">{t('raised')}</p>
                    </div>
                    <Link href={leaderboardHref} className="text-center group">
                      <p className="text-2xl font-bold text-stone-800 group-hover:text-dgr-gold-hover transition-colors">
                        {format.number(eventData.participants)}
                      </p>
                      <p className="text-stone-500 text-sm group-hover:text-dgr-gold-hover transition-colors">
                        {t('participants')}
                      </p>
                    </Link>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-stone-600">
                        {t('goalReached', { percent: progressPercent.toFixed(0) })}
                      </span>
                      <span className="text-stone-500">
                        {t(
                          'amountToGo',
                          { amount: eventData.goal - eventData.raised },
                          {
                            number: {
                              currency: { style: 'currency', currency: regionalCurrency },
                            },
                          },
                        )}
                      </span>
                    </div>
                    <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-dgr-gold to-dgr-gold-hover rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-dgr-gold-hover">
                      {formatCompactCurrency(eventData.raised)}
                    </p>
                    <p className="text-stone-500 text-sm">{t('raised')}</p>
                  </div>
                  <Link href={leaderboardHref} className="text-center group">
                    <p className="text-2xl font-bold text-stone-800 group-hover:text-dgr-gold-hover transition-colors">
                      {format.number(eventData.participants)}
                    </p>
                    <p className="text-stone-500 text-sm group-hover:text-dgr-gold-hover transition-colors">
                      {t('participants')}
                    </p>
                    {eventData.capacity !== null && (
                      <p className="text-stone-400 text-xs mt-1">
                        {registerT('capacityBadge', {
                          capacity: format.number(eventData.capacity),
                        })}
                      </p>
                    )}
                  </Link>
                </div>
              )}
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center gap-8">
              {eventData.goal > 0 ? (
                <div className="flex-1">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-dgr-gold-hover">
                        {formatCompactCurrency(eventData.raised)}
                      </span>
                      <span className="text-stone-400">/</span>
                      <span className="text-xl font-semibold text-stone-600">
                        {formatCompactCurrency(eventData.goal)}
                      </span>
                    </div>
                    <span className="text-sm text-stone-500">
                      {t('goalReached', { percent: progressPercent.toFixed(0) })}
                    </span>
                  </div>
                  <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-dgr-gold to-dgr-gold-hover rounded-full transition-all duration-500"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-dgr-gold-hover">
                      {formatCompactCurrency(eventData.raised)}
                    </p>
                    <p className="text-stone-500 text-sm">{t('raised')}</p>
                  </div>
                  <div className="w-px h-12 bg-stone-200" />
                  <Link href={leaderboardHref} className="text-center group">
                    <p className="text-3xl font-bold text-stone-800 group-hover:text-dgr-gold-hover transition-colors">
                      {format.number(eventData.participants)}
                    </p>
                    <p className="text-stone-500 text-sm group-hover:text-dgr-gold-hover transition-colors">
                      {t('participants')}
                    </p>
                    {eventData.capacity !== null && (
                      <p className="text-stone-400 text-xs mt-1">
                        {registerT('capacityBadge', {
                          capacity: format.number(eventData.capacity),
                        })}
                      </p>
                    )}
                  </Link>
                </>
              )}

              {/* Divider + Participants (only when goal view shows progress bar) */}
              {eventData.goal > 0 && (
                <>
                  <div className="w-px h-12 bg-stone-200" />
                  <div className="flex items-center gap-8">
                    <Link href={leaderboardHref} className="text-center group">
                      <p className="text-3xl font-bold text-stone-800 group-hover:text-dgr-gold-hover transition-colors">
                        {format.number(eventData.participants)}
                      </p>
                      <p className="text-stone-500 text-sm group-hover:text-dgr-gold-hover transition-colors">
                        {t('participants')}
                      </p>
                      {eventData.capacity !== null && (
                        <p className="text-stone-400 text-xs mt-1">
                          {registerT('capacityBadge', {
                            capacity: format.number(eventData.capacity),
                          })}
                        </p>
                      )}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 bg-dgr-cream">
          <div className="container max-w-6xl">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {isLoadingEvent && (
                  <div className="bg-white rounded-xl border border-stone-200 p-10 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
                  </div>
                )}

                {!canViewRideDetails && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <p className="text-amber-900 font-medium">
                      {t('registeredRiderOnlyWarning', { eventName: eventData.city })}
                    </p>
                    <p className="text-amber-700 mt-2 text-sm">
                      {isAuthenticated
                        ? t('changeRideToViewInfo', { eventName: eventData.city })
                        : t('loginToViewInfo')}
                    </p>
                    {isAuthenticated && canShowChangeRide && (
                      <Button
                        onClick={openChangeRideModal}
                        className="mt-4 bg-dgr-gold hover:bg-dgr-gold-hover text-white"
                      >
                        <Settings2 className="w-4 h-4 mr-2" />
                        {tMy('changeRide')}
                      </Button>
                    )}
                    {!isAuthenticated && (
                      <Button
                        asChild
                        className="mt-4 bg-dgr-gold hover:bg-dgr-gold-hover text-white"
                      >
                        <Link href={registerRideHref}>{t('registerNow')}</Link>
                      </Button>
                    )}
                  </div>
                )}

                {/* About */}
                {canViewRideDetails && hasDescription && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">{t('aboutThisRide')}</h2>
                    <div
                      className="prose prose-stone max-w-none prose-p:text-stone-600"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(eventData.description ?? ''),
                      }}
                    />
                  </div>
                )}

                {/* Schedule & Meeting Point */}
                {canViewRideDetails && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">
                      {t('scheduleMeetingPoint')}
                    </h2>

                    {/* Meeting Point Info */}
                    <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg mb-6">
                      <div className="w-10 h-10 rounded-lg bg-dgr-gold/20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-dgr-gold-hover" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-stone-900">
                          {eventData.venue.name ?? eventData.city}
                        </h3>
                        {(eventData.venue.address || eventData.venue.city) && (
                          <p className="text-stone-600 text-sm">
                            {[eventData.venue.address, eventData.venue.city]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                        {eventData.venue.name && (
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(
                              [eventData.venue.name, eventData.venue.address, eventData.venue.city]
                                .filter(Boolean)
                                .join(', '),
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-sm text-dgr-gold-hover hover:text-dgr-gold-hover font-medium"
                          >
                            <Navigation className="w-4 h-4" />
                            {t('getDirections')}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>

                    {hasRouteTimeline && hasTimelineItems ? (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-stone-500 flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5" />
                            {t('route')}
                          </h4>
                          {eventData.distance !== null && (
                            <span className="inline-flex items-center rounded-md bg-stone-100 px-2 py-1 text-sm text-stone-700">
                              {eventData.distance} {eventData.distanceFormat ?? 'km'}
                            </span>
                          )}
                        </div>
                        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                          <div className="space-y-0">
                            {timelineItems.map((item, i) => (
                              <div
                                key={`${item.title}-${item.time}-${i}`}
                                className="relative flex gap-4"
                              >
                                {i < timelineItems.length - 1 && (
                                  <div className="absolute left-[51px] top-8 bottom-0 w-0.5 bg-stone-200" />
                                )}
                                <div className="w-20 flex-shrink-0 pt-1">
                                  <span className="text-base font-semibold text-dgr-gold-hover">
                                    {item.time}
                                  </span>
                                </div>
                                <div className="flex-1 pb-5">
                                  <h3 className="text-base font-semibold text-stone-900">
                                    {item.title}
                                  </h3>
                                  {item.location && (
                                    <p className="text-sm text-stone-500 mt-1 flex items-center gap-1.5">
                                      <MapPin className="w-3.5 h-3.5 text-stone-400" />
                                      {item.location}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                        <p className="text-stone-600">{t('scheduleCurrentlyBeingAdded')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Route Map */}
                {canViewRideDetails && (mapPoint || hasStaticMapLink || hasRouteMap) ? (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">{t('routeMap')}</h2>
                    {hasRouteMap || mapPoint ? (
                      <div className="relative rounded-lg overflow-hidden border border-stone-200">
                        <div ref={mapContainer} className="h-[400px] w-full" />

                        {/* Loading overlay */}
                        {!mapInitialized && MAPBOX_TOKEN && (
                          <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
                            <div className="text-center">
                              <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-2" />
                              <p className="text-sm text-stone-500">{t('loadingMap')}</p>
                            </div>
                          </div>
                        )}

                        {/* No token message */}
                        {!MAPBOX_TOKEN && (
                          <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
                            <div className="text-center">
                              <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                              <h3 className="text-lg font-semibold text-stone-600">
                                {t('routeMap')}
                              </h3>
                              <p className="text-sm text-stone-500 max-w-sm mx-auto mt-2">
                                Map not available
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Legend */}
                        {hasRouteMap && routeMapData?.distanceMeters ? (
                          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg px-3 py-2">
                            <div className="flex items-center gap-3 text-xs">
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow-sm" />
                                <span className="text-stone-600">{t('start') ?? 'Start'}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow-sm" />
                                <span className="text-stone-600">{t('finish') ?? 'Finish'}</span>
                              </div>
                              <span className="text-stone-500 font-medium">
                                {routeMapData.distanceFormat === 'mi'
                                  ? `${(routeMapData.distanceMeters / 1609.344).toFixed(1)} mi`
                                  : `${(routeMapData.distanceMeters / 1000).toFixed(1)} km`}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg px-3 py-2">
                            <div className="flex items-center gap-2 text-xs">
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-dgr-gold-hover border-2 border-white shadow-sm" />
                                <span className="text-stone-600">{eventData.city}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-stone-200 bg-stone-50 p-6">
                        <a
                          href={eventData.mapLink ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-dgr-gold-hover hover:text-dgr-gold-hover font-medium"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t('openRouteMap')}
                        </a>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Hosts */}
                {eventData.hosts.length > 0 && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h2 className="text-xl font-bold text-stone-900 mb-4">{t('eventHosts')}</h2>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {eventData.hosts.map((host, i) => {
                        const avatar = (
                          <div className="w-12 h-12 rounded-full bg-stone-200 overflow-hidden flex-shrink-0">
                            {host.profileImageUrl ? (
                              /* eslint-disable-next-line @next/next/no-img-element */
                              <img
                                src={host.profileImageUrl}
                                alt={host.name}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-stone-500 font-medium">
                                {host.name
                                  .split(' ')
                                  .map((namePart) => namePart[0])
                                  .join('')}
                              </div>
                            )}
                          </div>
                        );
                        const info = (
                          <div>
                            <p className="font-medium text-stone-900">{host.name}</p>
                            <p className="text-sm text-stone-500">
                              {host.isPrimary ? t('rideHost') : t('host')}
                            </p>
                          </div>
                        );

                        return host.username ? (
                          <Link
                            key={i}
                            href={`/fundraiser/${host.username}`}
                            className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-stone-50 transition-colors"
                          >
                            {avatar}
                            {info}
                          </Link>
                        ) : (
                          <div key={i} className="flex items-center gap-3">
                            {avatar}
                            {info}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Donors */}
                {(!donorsData || donorsData.donations.length > 0 || isLoadingDonors) && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Heart className="w-5 h-5 text-dgr-gold" />
                      <h2 className="text-xl font-bold text-stone-900">{t('recentDonors')}</h2>
                    </div>

                    {isLoadingDonors || !donorsData ? (
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-3 animate-pulse">
                            <div className="w-10 h-10 rounded-full bg-stone-200" />
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-stone-200 rounded w-1/3" />
                              <div className="h-3 bg-stone-100 rounded w-2/3" />
                            </div>
                            <div className="h-4 bg-stone-200 rounded w-16" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3">
                          {donorsData.donations.map((donation) => (
                            <div
                              key={donation.id}
                              className="flex items-start gap-3 py-2 border-b border-stone-100 last:border-b-0"
                            >
                              <div
                                className={cn(
                                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium',
                                  donation.isAnonymous
                                    ? 'bg-stone-100 text-stone-400'
                                    : 'bg-dgr-gold/10 text-dgr-gold-hover',
                                )}
                              >
                                {donation.isAnonymous ? (
                                  <Heart className="w-4 h-4" />
                                ) : (
                                  donation.donorName
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')
                                    .slice(0, 2)
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="font-medium text-stone-900 text-sm truncate">
                                    {donation.donorName}
                                  </p>
                                  <span className="text-sm font-semibold text-dgr-gold-hover flex-shrink-0">
                                    {donation.currencySymbol}
                                    {donation.amount.toLocaleString(undefined, {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                                {donation.message && (
                                  <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">
                                    &ldquo;{donation.message}&rdquo;
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Pagination */}
                        {donorsData.total > donorPageSize && (
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
                            <p className="text-xs text-stone-400">
                              {t('showingDonations', {
                                start: donorPage * donorPageSize + 1,
                                end: Math.min((donorPage + 1) * donorPageSize, donorsData.total),
                                total: donorsData.total,
                              })}
                            </p>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDonorPage((p) => Math.max(0, p - 1))}
                                disabled={donorPage === 0}
                                className="h-7 px-2 text-xs"
                              >
                                <ChevronLeft className="w-3 h-3 mr-1" />
                                {t('previousPage')}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDonorPage((p) => p + 1)}
                                disabled={!donorsData.hasMore}
                                className="h-7 px-2 text-xs"
                              >
                                {t('nextPage')}
                                <ChevronRight className="w-3 h-3 ml-1" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Event Sponsors */}
                <SponsorLogosSection
                  eventId={eventResponse?.id}
                  title={t('eventSponsors')}
                  variant="light"
                />
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6">
                {/* CTA Card */}
                <div className="bg-stone-900 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-2 mb-4">
                    <Heart className="w-5 h-5 text-dgr-gold" />
                    <h3 className="font-semibold">{t('joinTheRide')}</h3>
                  </div>
                  <p className="text-white/70 text-sm mb-4">{t('joinDescription')}</p>
                  <div className="space-y-2">
                    {eventData.registrationOpen ? (
                      <>
                        {!isAuthenticated && (
                          <Button
                            asChild
                            className="w-full bg-dgr-gold hover:bg-dgr-gold-hover text-white"
                          >
                            <Link href={registerRideHref}>{t('registerToRide')}</Link>
                          </Button>
                        )}
                        {/* TODO: Re-enable when Facebook event integration is ready
                        {eventData.facebookEventUrl && (
                          <Button
                            asChild
                            variant="outline"
                            className="w-full border-white/30 text-black hover:bg-white/10"
                          >
                            <a
                              href={eventData.facebookEventUrl ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('facebookEvent')}
                            </a>
                          </Button>
                        )}
                        */}
                      </>
                    ) : (
                      <p className="text-center text-white/60 text-sm">{t('registrationClosed')}</p>
                    )}
                  </div>
                </div>

                {/* Donate to this Event */}
                {strategy.isFundraisingActive ? (
                  <DonationAmountCard
                    recipientName={eventData.city}
                    currency={strategy.currency}
                    onDonate={(amount) => {
                      setDonateInitialAmount(amount);
                      setIsDonateModalOpen(true);
                    }}
                  />
                ) : (
                  <div className="bg-stone-900 rounded-xl p-6 border border-stone-800 text-center">
                    <p className="text-stone-400 text-sm">{tDonate('fundraisingDisabled')}</p>
                  </div>
                )}

                {/* Top Fundraisers */}
                {eventData.topFundraisers.length > 0 && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Trophy className="w-5 h-5 text-dgr-gold" />
                      <h3 className="font-semibold text-stone-900">{t('topFundraisers')}</h3>
                    </div>
                    <div className="space-y-3">
                      {eventData.topFundraisers.map((fundraiser, i) => (
                        <Link
                          key={i}
                          href={`/fundraiser/${fundraiser.username}`}
                          className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-stone-50 transition-colors"
                        >
                          <RankBadge rank={i + 1} />
                          <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0">
                            {fundraiser.image ? (
                              <>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                  src={fundraiser.image}
                                  alt={fundraiser.name}
                                  className="object-cover w-full h-full"
                                />
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-stone-500 text-xs font-medium">
                                {fundraiser.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-900 text-sm truncate">
                              {fundraiser.name}
                            </p>
                          </div>
                          <span className="text-sm font-semibold text-dgr-gold-hover">
                            {formatCompactCurrency(fundraiser.raised)}
                          </span>
                        </Link>
                      ))}
                    </div>
                    <Link
                      href={leaderboardHref}
                      className="block mt-4 text-center text-sm text-dgr-gold-hover hover:text-dgr-gold-hover font-medium"
                    >
                      {t('viewFullLeaderboard')}
                    </Link>
                  </div>
                )}

                {/* View All Riders */}
                {eventData.participants > 0 && (
                  <Link
                    href={leaderboardHref}
                    className="block bg-white rounded-xl border border-stone-200 p-6 hover:border-dgr-gold/30 hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-dgr-gold/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-dgr-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-stone-900 group-hover:text-dgr-gold-hover transition-colors">
                          {t('viewAllRiders')}
                        </p>
                        <p className="text-stone-500 text-sm">
                          {t('ridersCount', { count: eventData.participants })}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-dgr-gold transition-colors" />
                    </div>
                  </Link>
                )}

                {/* Mobile App Promo */}
                <div className="bg-white rounded-xl border border-stone-200 p-6 hidden">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-dgr-gold" />
                    <h3 className="font-semibold text-stone-900">{t('dgrApp')}</h3>
                  </div>
                  <p className="text-stone-600 text-sm mb-4">{t('dgrAppDescription')}</p>
                  <div className="flex gap-2">
                    <a
                      href="https://apps.apple.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors text-stone-700"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                      <span className="text-xs font-medium">iOS</span>
                    </a>
                    <a
                      href="https://play.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors text-stone-700"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                      <span className="text-xs font-medium">Android</span>
                    </a>
                  </div>
                </div>

                {isAuthenticated && canViewRideDetails && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h3 className="font-semibold text-stone-900 mb-4">{t('questions')}</h3>
                    <p className="text-stone-600 text-sm mb-4">{t('questionsDescription')}</p>
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="contact-hosts" className="border-b-0 border rounded-lg">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-stone-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-stone-500" />
                            <span className="text-sm font-medium">{t('contactHosts')}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4">
                          <p className="text-xs text-stone-500 mb-3">
                            Sending to {eventData.hosts.length} host
                            {eventData.hosts.length > 1 ? 's' : ''}
                          </p>

                          {contactHostsStatus === 'success' && (
                            <p className="text-sm text-green-600 mb-3">
                              {t('contactHostsSuccess')}
                            </p>
                          )}
                          {contactHostsStatus === 'error' && (
                            <p className="text-sm text-red-600 mb-3">{t('contactHostsError')}</p>
                          )}

                          <Textarea
                            placeholder={t('writeMessage')}
                            value={contactMessage}
                            onChange={(e) => {
                              setContactMessage(e.target.value);
                              if (contactHostsStatus !== 'idle') setContactHostsStatus('idle');
                            }}
                            className="mb-3 min-h-[100px]"
                            disabled={contactHostsMutation.isPending}
                          />
                          <Button
                            className="w-full bg-dgr-gold hover:bg-dgr-gold-hover text-white"
                            disabled={
                              !contactMessage ||
                              contactHostsMutation.isPending ||
                              eventData.id === 'unknown'
                            }
                            onClick={() =>
                              contactHostsMutation.mutate({
                                eventId: eventData.id,
                                message: contactMessage,
                              })
                            }
                          >
                            {contactHostsMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {t('contactHostsSending')}
                              </>
                            ) : (
                              t('sendToAll')
                            )}
                          </Button>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <DonationFormModal
        open={isDonateModalOpen}
        onOpenChange={(open) => {
          setIsDonateModalOpen(open);
          if (!open) setDonateInitialAmount(null);
        }}
        initialAmount={donateInitialAmount}
        recipientName={eventData.city}
        strategy={strategy}
        returnDonationId={returnDonationId ?? undefined}
        redirectResult={redirectResult}
        returnSessionId={returnSessionId}
        recoveryToken={recoveryToken}
        isLocalStorageRecovery={isLocalStorageRecovery}
        onSubmit={handleDonationSuccess}
        onRedirectSuccess={handleRedirectSuccess}
      />

      <Dialog open={isChangeRideOpen} onOpenChange={setIsChangeRideOpen}>
        <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-xl">
          <DialogHeader>
            <DialogTitle>{tMy('changeRideForm.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-stone-500">{tMy('changeRideForm.description')}</p>
            {changeRideSuccess && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700">
                {changeRideSuccess}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-stone-700">{tMy('changeRideForm.country')}</Label>
              <CountrySelect
                countries={countriesData.map((country) => ({
                  code: country.code,
                  name: country.name,
                }))}
                value={changeRideCountryCode}
                onValueChange={(countryCode) => {
                  setChangeRideCountryCode(countryCode);
                  setChangeRideStateCode('');
                  setChangeRideRideId('');
                  setChangeRideError(null);
                }}
                placeholder={tMy('changeRideForm.countryPlaceholder')}
                disabled={isLoadingCountries || changeRideMutation.isPending}
                className="h-11"
              />
            </div>

            {changeRideCountryCode && hasStates && (
              <div className="space-y-2">
                <Label className="text-stone-700">{tMy('changeRideForm.stateRegion')}</Label>
                <Combobox
                  options={statesData.map((state: StateData) => ({
                    value: state.code,
                    label: state.name,
                  }))}
                  value={changeRideStateCode}
                  onValueChange={(stateCode) => {
                    setChangeRideStateCode(stateCode);
                    setChangeRideRideId('');
                    setChangeRideError(null);
                  }}
                  placeholder={tMy('changeRideForm.statePlaceholder')}
                  searchPlaceholder={tMy('changeRideForm.stateSearchPlaceholder')}
                  disabled={isLoadingStates || changeRideMutation.isPending}
                  className="h-11"
                />
              </div>
            )}

            {((hasStates && changeRideStateCode) || (!hasStates && changeRideCountryCode)) && (
              <div className="space-y-2">
                <Label className="text-stone-700">{tMy('changeRideForm.ride')}</Label>
                <Select
                  value={changeRideRideId}
                  onValueChange={(rideId) => {
                    setChangeRideRideId(rideId);
                    setChangeRideError(null);
                  }}
                  disabled={isLoadingRides || changeRideMutation.isPending}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={tMy('changeRideForm.ridePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    {ridesData.length === 0 && !isLoadingRides ? (
                      <div className="p-4 text-center text-stone-500">
                        {tMy('changeRideForm.noRidesAvailable')}
                      </div>
                    ) : (
                      ridesData.map((ride: RideData) => {
                        const capacityBadge = getCapacityBadgeValue(ride.capacity);
                        const minimumDonationBadge = parseRideMinimumDonation(ride.minimumDonation);
                        return (
                          <SelectItem key={ride.id} value={ride.id} className="py-3">
                            <div className="flex w-full items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <MapPin className="w-4 h-4 shrink-0 text-stone-500" />
                                <span className="truncate">{ride.name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {capacityBadge && (
                                  <Badge className="border-stone-300 bg-stone-100 text-stone-700">
                                    {tMy('changeRideForm.capacityBadge', {
                                      capacity: capacityBadge,
                                    })}
                                  </Badge>
                                )}
                                {minimumDonationBadge > 0 && (
                                  <Badge className="border-stone-300 bg-stone-100 text-stone-700">
                                    {tMy('changeRideForm.minimumDonationBadge', {
                                      amount: `${currencySymbol}${minimumDonationBadge.toLocaleString()}`,
                                    })}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {changeRideRideId && isSelectedRideFull && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <span>{tMy('changeRideForm.capacityFullWarning')}</span>
              </div>
            )}

            {changeRideRideId && selectedRideMinimumDonation > 0 && !isMinimumDonationMet && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <span>
                  {tMy('changeRideForm.minimumDonationWarning', {
                    required: `${currencySymbol}${selectedRideMinimumDonation.toLocaleString()}`,
                    current: `${currencySymbol}${entrantTotalRaised.toLocaleString()}`,
                    shortfall: `${currencySymbol}${minimumDonationShortfall.toLocaleString()}`,
                  })}
                </span>
              </div>
            )}

            {changeRideError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
                {changeRideError}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setIsChangeRideOpen(false)}
              disabled={changeRideMutation.isPending}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={handleChangeRideSave}
              disabled={isSaveDisabled}
              className="bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light disabled:opacity-50"
            >
              {changeRideMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {tMy('changeRideForm.saving')}
                </span>
              ) : (
                tMy('changeRideForm.save')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
